import { ActionTree } from 'vuex';
import { EDirectoryAction } from '../directories/store/directory.constants';
import { ILoadResourcesFilter } from '@/content-library/types/load-resources-filter';
import { ContentLibraryHelper } from '@/content-library/helpers/content-library.helper';
import {
  EContentLibraryAction,
  EContentLibraryMutation,
  state as defaultState,
} from '@/content-library/store/content-library.constants';
import { IContentLibraryState } from '@/content-library/store/content-library.types';
import { RootState } from '@/store/store';
import { IResource } from '@/api/resources/types/resource';
import { EResourceType } from '@/api/resources/types/enum/resource-type';
import { ResourceService } from '@/api/resources/services/resource.service';
import { ContentLinkBlockService } from '@/api/content-link/services/content-link-block.service';
import { DocumentService } from '@/api/documents/services/document.service';
import { IEditorBlock } from '@/editor/types';
import { IDocument } from '@lib-shared/types/Document';
import { IVideo } from '@/types/Video';
import { IContentLinkBlock } from '@/content-link/types/content-link';
import { translate } from '@/helpers/translation.helper';
import { EditorBlockService } from '@/editor/api/services/editor-block.service';
import { VideoService } from '@/api/video/services/video.service';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';

export const actions: ActionTree<IContentLibraryState, RootState> = {
  async [EContentLibraryAction.Load]({ dispatch, commit, state }, shouldLoadDirectories = true) {
    commit(EContentLibraryMutation.SetLoaded, false);
    commit(EContentLibraryMutation.SetLoading, true);
    const { limit } = state.payload;
    const { searchTerms, sortBy, sortOrder, type, directory } = state.payload.filter;
    try {
      const {
        resources,
        totalCount,
      }: { resources: IResource[]; totalCount: number } = await ResourceService.loadList(
        searchTerms,
        limit,
        0,
        sortBy,
        sortOrder,
        type,
        directory
      );
      dispatch(EContentLibraryAction.LoadSuccess, { resources, totalCount });
      if (shouldLoadDirectories) {
        dispatch(`directories/${EDirectoryAction.Load}`, directory);
      }
    } catch {
      dispatch(EContentLibraryAction.LoadFailed);
    }
  },
  [EContentLibraryAction.LoadSuccess](
    { commit },
    { resources, totalCount }: { resources: IResource[]; totalCount: number }
  ) {
    commit(EContentLibraryMutation.SetResources, resources);
    commit(EContentLibraryMutation.SetTotalCount, totalCount);
    commit(EContentLibraryMutation.SetPagination, {
      limit: 12,
      offset: resources.length,
    });
    commit(EContentLibraryMutation.SetLoading, false);
    commit(EContentLibraryMutation.SetLoaded, true);
  },
  [EContentLibraryAction.LoadFailed]({ commit }) {
    commit(EContentLibraryMutation.SetLoaded, false);
    commit(EContentLibraryMutation.SetLoading, false);
  },
  async [EContentLibraryAction.LoadMore]({ dispatch, commit, state }) {
    commit(EContentLibraryMutation.SetLoading, true);
    const { limit, offset } = state.payload;
    const { searchTerms, sortBy, sortOrder, type, directory } = state.payload.filter;
    try {
      const { resources }: { resources: IResource[] } = await ResourceService.loadList(
        searchTerms,
        limit,
        offset,
        sortBy,
        sortOrder,
        type,
        directory
      );
      const allResources: IResource[] = [...state.resources, ...resources];
      dispatch(EContentLibraryAction.LoadMoreSuccess, allResources);
    } catch {
      dispatch(EContentLibraryAction.LoadMoreFailed);
    }
  },
  [EContentLibraryAction.LoadMoreSuccess]({ commit, state }, resources: IResource[]) {
    commit(EContentLibraryMutation.SetResources, resources);
    commit(EContentLibraryMutation.SetPagination, {
      limit: state.payload.limit,
      offset: resources.length,
    });
    commit(EContentLibraryMutation.SetLoading, false);
  },
  [EContentLibraryAction.LoadMoreFailed]({ commit }) {
    commit(EContentLibraryMutation.SetLoading, false);
  },
  async [EContentLibraryAction.LoadThumbnail](
    { state, commit, dispatch },
    { resourceId, contentId, type }: { resourceId: string; contentId: string; type: EResourceType }
  ) {
    const resourcesLoading: IResource[] = state.resources.map((resource: IResource) =>
      resource.id === resourceId ? { ...resource, loading: true } : resource
    );
    commit(EContentLibraryMutation.SetResources, resourcesLoading);
    let block: Partial<IContentLinkBlock | IEditorBlock | IVideo | IDocument>;
    switch (type) {
      case EResourceType.ContentLink: {
        try {
          block = await ContentLinkBlockService.loadThumbnail(contentId);
        } catch (e) {
          console.error(
            `Could not load thumbnail for ${EResourceType.ContentLink} resource`,
            resourceId
          );
        }
        break;
      }
      case EResourceType.Video: {
        try {
          block = await VideoService.loadThumbnail(contentId);
        } catch (e) {
          console.error(`Could not load thumbnail for ${EResourceType.Video} resource`, resourceId);
        }
        break;
      }
      case EResourceType.Template: {
        try {
          block = await DocumentService.loadTemplateResourceDetails(contentId);
        } catch (e) {
          console.error(
            `Could not load thumbnail for ${EResourceType.Template} resource`,
            resourceId
          );
        }
        break;
      }
      case EResourceType.Editor: {
        try {
          block = await EditorBlockService.loadEditorBlock(contentId);
        } catch (e) {
          console.error(
            `Could not load thumbnail for ${EResourceType.Editor} resource`,
            resourceId
          );
        }
        break;
      }
      default:
        break;
    }
    dispatch(EContentLibraryAction.LoadThumbnailSuccess, { resourceId, block });
  },
  [EContentLibraryAction.LoadThumbnailSuccess](
    { commit, state },
    {
      resourceId,
      block,
    }: { resourceId: string; block: IContentLinkBlock | IEditorBlock | IDocument | IVideo }
  ) {
    const resourcesWithBlock: IResource[] = state.resources.map((resource: IResource) =>
      resource.id === resourceId ? { ...resource, block, loading: false } : resource
    );
    commit(EContentLibraryMutation.SetResources, resourcesWithBlock);
  },
  async [EContentLibraryAction.LoadDetails]({ commit, dispatch }, resourceId: string) {
    commit(EContentLibraryMutation.SetLoading, true);
    try {
      const details: IResource = await ResourceService.loadDetails(resourceId);
      let block: IVideo | IContentLinkBlock | IEditorBlock | IDocument = null;
      switch (details.resourceType) {
        case EResourceType.ContentLink: {
          block = await ContentLinkBlockService.loadDetails(details.contentId);
          break;
        }
        case EResourceType.Video: {
          block = await VideoService.loadVideo(details.contentId);
          break;
        }
        case EResourceType.Editor: {
          block = await EditorBlockService.loadEditorBlock(details.contentId);
          break;
        }
        case EResourceType.Template: // Loading template details when entering edit page for template
        default:
          break;
      }
      dispatch(EContentLibraryAction.LoadDetailsSuccess, { ...details, block });
    } catch (e) {
      commit(EContentLibraryMutation.SetLoading, false);
    }
  },
  [EContentLibraryAction.LoadDetailsSuccess]({ commit, state }, resource: IResource) {
    const updatedResoures: IResource[] = ContentLibraryHelper.addResourceDetails(
      [...state.resources],
      resource
    );
    commit(EContentLibraryMutation.SetResources, updatedResoures);
    commit(EContentLibraryMutation.SetLoading, false);
  },
  [EContentLibraryAction.UpdateResource]({ commit, state }, resource: IResource) {
    const updatedStateResources: IResource[] = state.resources.map((stateResource: IResource) =>
      stateResource.id === resource.id ? resource : stateResource
    );
    commit(EContentLibraryMutation.SetResources, updatedStateResources);
  },
  [EContentLibraryAction.Reset]({ commit }) {
    commit(EContentLibraryMutation.SetState, defaultState);
  },
  [EContentLibraryAction.UpdateFilter](
    { commit, dispatch, state },
    attribute: Partial<ILoadResourcesFilter>
  ) {
    const isInitialLoad = !state.loaded;
    const newFilter: ILoadResourcesFilter = { ...state.payload.filter, ...attribute };
    const defaultStateWithFilters: IContentLibraryState = ContentLibraryHelper.getDefaultStateWithFilters(
      { ...state },
      defaultState
    );
    const hasDirectoryChanged = state.payload.filter.directory !== newFilter.directory;
    const shouldLoadDirectories: boolean = hasDirectoryChanged || isInitialLoad;

    commit(EContentLibraryMutation.SetState, defaultStateWithFilters);
    commit(EContentLibraryMutation.SetFilter, newFilter);
    dispatch(EContentLibraryAction.Load, shouldLoadDirectories);
  },
  async [EContentLibraryAction.ImplementResourceInDocument](
    { dispatch },
    {
      documentId,
      resourceId,
      contentId,
    }: { documentId: string; resourceId: string; contentId: string }
  ) {
    try {
      await ResourceService.implementInDocument({ documentId, resourceId, contentId });
    } catch (e) {
      dispatch(EContentLibraryAction.ImplementResourceInDocumentFailed);
    }
  },
  [EContentLibraryAction.ImplementResourceInDocumentFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-implement-resource')
    );
  },
  [EContentLibraryAction.RemoveResource]({ commit, state }, resource: IResource) {
    const updatedResource: IResource[] = state.resources?.length
      ? state.resources.filter((stateResource: IResource) => stateResource.id !== resource.id)
      : [];
    const updatedTotalCount: number = state.totalCount - 1 > 0 ? state.totalCount - 1 : 0;
    commit(EContentLibraryMutation.SetTotalCount, updatedTotalCount);
    commit(EContentLibraryMutation.SetResources, updatedResource);
  },
};
